import React, { useEffect, useRef } from 'react';
import { Box, Typography, Paper, Tooltip, IconButton } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import { AIMessage } from './types/ai.types';
import { ResultsRenderer } from './ResultsRenderer';

interface Props {
    messages: AIMessage[];
    onFollowUp?: (question: string) => void;
}

const ChatMessages: React.FC<Props> = ({ messages, onFollowUp }) => {
    const bottomRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Scroll to bottom on new messages
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    if (messages.length === 0) return null;

    return (
        <Box
            ref={containerRef}
            sx={{
                flex: 1,
                overflowY: 'auto',   // ← messages scroll internally
                minHeight: 0,        // ← critical flex fix without this it won't scroll
                px: 1.5,
                py: 1.25,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
            }}
        >
            {messages.map((msg, index) => (
                <MessageItem
                    key={msg.id}
                    message={msg}
                    isLast={index === messages.length - 1}
                    onFollowUp={onFollowUp}
                />
            ))}
            <div ref={bottomRef} />
        </Box>
    );
};

// ── Individual message ───────────────────────────────────────────────

interface MessageItemProps {
    message: AIMessage;
    isLast: boolean;
    onFollowUp?: (question: string) => void;
}

const MessageItem: React.FC<MessageItemProps> = ({ message, isLast, onFollowUp }) => {
    const isUser = message.role === 'user';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isUser ? 'flex-end' : 'flex-start',
                gap: 0.5,
            }}
        >
            {isUser
                ? <UserBubble message={message} />
                : <AssistantBubble message={message} isLast={isLast} onFollowUp={onFollowUp} />
            }
        </Box>
    );
};

// ── User bubble ──────────────────────────────────────────────────────

const UserBubble: React.FC<{ message: AIMessage }> = ({ message }) => (
    <>
        <Box
            sx={{
                px: 1.5,
                py: 1,
                bgcolor: '#EEEDFE',
                borderRadius: '10px 10px 3px 10px',
                maxWidth: '88%',
                fontSize: 13,
                lineHeight: 1.55,
                color: '#3C3489',
            }}
        >
            {message.content}
        </Box>
        <Timestamp date={message.timestamp} />
    </>
);

// ── Assistant bubble ─────────────────────────────────────────────────

interface AssistantBubbleProps {
    message: AIMessage;
    isLast: boolean;
    onFollowUp?: (question: string) => void;
}

const AssistantBubble: React.FC<AssistantBubbleProps> = ({ message, isLast, onFollowUp }) => {
    const [copied, setCopied] = React.useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(message.content);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <>
            {/* AI label */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                <Box sx={{
                    width: 18, height: 18, borderRadius: 0.75,
                    bgcolor: '#EEEDFE', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                }}>
                    <AutoFixHighOutlinedIcon sx={{ fontSize: 11, color: '#534AB7' }} />
                </Box>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>
                    Collection intelligence
                </Typography>
            </Box>

            {/* Bubble */}
            <Box sx={{ maxWidth: '100%', width: '100%' }}>
                {message.isStreaming ? (
                    <TypingIndicator />
                ) : (
                    <Paper
                        variant="outlined"
                        sx={{
                            px: 1.5,
                            py: 1,
                            borderRadius: '3px 10px 10px 10px',
                            fontSize: 13,
                            lineHeight: 1.6,
                            color: 'text.primary',
                            bgcolor: 'background.default',
                        }}
                    >
                        {message.content}
                    </Paper>
                )}

                {/* Result card — table, chart, metric */}
                {!message.isStreaming && message.result && (
                    <Box sx={{ mt: 0.75 }}>
                        <ResultsRenderer result={message.result} />
                    </Box>
                )}
            </Box>

            {/* Actions row — only when not streaming */}
            {!message.isStreaming && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    <Timestamp date={message.timestamp} />
                    <Tooltip title={copied ? 'Copied!' : 'Copy response'}>
                        <IconButton
                            size="small"
                            onClick={handleCopy}
                            sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
                        >
                            <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </>
    );
};

// ── Typing indicator ─────────────────────────────────────────────────

const TypingIndicator: React.FC = () => (
    <Paper
        variant="outlined"
        sx={{
            px: 1.5, py: 1,
            borderRadius: '3px 10px 10px 10px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            bgcolor: 'background.default',
        }}
    >
        {[0, 1, 2].map(i => (
            <Box
                key={i}
                sx={{
                    width: 5, height: 5,
                    borderRadius: '50%',
                    bgcolor: 'text.disabled',
                    animation: 'aiPulse 1.2s infinite',
                    animationDelay: `${i * 0.2}s`,
                    '@keyframes aiPulse': {
                        '0%, 80%, 100%': { opacity: 0.2, transform: 'scale(0.85)' },
                        '40%': { opacity: 1, transform: 'scale(1)' },
                    },
                }}
            />
        ))}
    </Paper>
);

// ── Timestamp ────────────────────────────────────────────────────────

const Timestamp: React.FC<{ date: Date }> = ({ date }) => (
    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
        {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </Typography>
);

export default ChatMessages;